/**
 * Google Identity Services (GSI) shared utilities.
 *
 * Centralises script loading and One Tap initialisation so that both
 * GoogleLoginButton (rendered button) and GoogleOneTap (auto-prompt)
 * share a single lifecycle without duplicating script tags or
 * re-initialising the GSI client.
 */

const GOOGLE_GSI_URL = "https://accounts.google.com/gsi/client";
const SCRIPT_ID = "google-gsi-script";
const GIS_READY_TIMEOUT_MS = 10000;
const GIS_POLL_INTERVAL_MS = 50;

// Injected at build time from the deploy env file.
const GOOGLE_CLIENT_ID = process.env.GOOGLE_CLIENT_ID;

let scriptPromise = null;
let initialized = false;
let credentialHandler = null;

function isGoogleReady() {
  return Boolean(window.google && window.google.accounts);
}

function waitForGoogleAccountsReady() {
  return new Promise((resolve, reject) => {
    const deadline = Date.now() + GIS_READY_TIMEOUT_MS;

    const check = () => {
      if (isGoogleReady()) {
        resolve();
        return;
      }

      if (Date.now() >= deadline) {
        reject(new Error("Google Identity Services not ready after script load"));
        return;
      }

      setTimeout(check, GIS_POLL_INTERVAL_MS);
    };

    check();
  });
}

/**
 * Load the GSI script exactly once. Returns a Promise that resolves
 * when `window.google.accounts` is available.
 */
function loadScript() {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    if (isGoogleReady()) {
      resolve();
      return;
    }

    if (document.getElementById(SCRIPT_ID)) {
      waitForGoogleAccountsReady().then(resolve).catch(error => {
        scriptPromise = null; // allow retry
        reject(error);
      });
      return;
    }

    const script = document.createElement("script");
    script.src = GOOGLE_GSI_URL;
    script.async = true;
    script.defer = true;
    script.id = SCRIPT_ID;
    script.onload = () => {
      waitForGoogleAccountsReady().then(resolve).catch(error => {
        scriptPromise = null; // allow retry
        reject(error);
      });
    };
    script.onerror = () => {
      scriptPromise = null; // allow retry
      reject(new Error("Failed to load Google Identity Services"));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

function initializeGoogle(clientId) {
  if (initialized || !clientId || !isGoogleReady()) return;
  initialized = true;
  window.google.accounts.id.initialize({
    client_id: clientId,
    callback: response => {
      if (credentialHandler) {
        credentialHandler(response);
      }
    },
    auto_select: true
  });
}

function setCredentialHandler(handler) {
  const previous = credentialHandler;
  credentialHandler = handler;
  return () => {
    if (credentialHandler === handler) {
      credentialHandler = previous;
    }
  };
}

function setOneTimeHandler(handler) {
  const previous = credentialHandler;
  const wrapped = response => {
    credentialHandler = previous;
    handler(response);
  };
  credentialHandler = wrapped;
  return () => {
    if (credentialHandler === wrapped) {
      credentialHandler = previous;
    }
  };
}

function promptOneTap(callback) {
  if (!isGoogleReady()) return;
  window.google.accounts.id.prompt(callback);
}

function initCodeClient({ clientId, redirectUri, scope, state }) {
  if (!window.google?.accounts?.oauth2?.initCodeClient) return null;
  return window.google.accounts.oauth2.initCodeClient({
    client_id: clientId,
    ux_mode: "redirect",
    redirect_uri: redirectUri,
    scope,
    state
  });
}

export {
  GOOGLE_CLIENT_ID,
  initializeGoogle,
  initCodeClient,
  loadScript,
  promptOneTap,
  setCredentialHandler,
  setOneTimeHandler
};
